import type { PocaConfig } from "../types";
import { gidToNumericId } from "./format";

export type AddToCartResult = {
  ok: boolean;
  message?: string;
};

const GENERIC_CART_ERROR_MESSAGES = new Set([
  "cart error",
  "error",
]);

function isGenericCartErrorMessage(message: string): boolean {
  return GENERIC_CART_ERROR_MESSAGES.has(message.trim().toLowerCase());
}

/** Shopify /cart/add.js 에러 본문(JSON 또는 plain text)에서 표시용 메시지 추출 */
export function parseCartAddErrorMessage(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "Could not add to cart";

  try {
    const data = JSON.parse(trimmed) as {
      message?: string;
      description?: string;
    };
    const message = data.message?.trim() ?? "";
    const description = data.description?.trim() ?? "";

    // e.g. message "Cart Error" + description "Cannot find variant"
    if (description && (!message || isGenericCartErrorMessage(message))) {
      return description;
    }

    if (message && description && message !== description) {
      return description;
    }

    return message || description || trimmed;
  } catch {
    return trimmed;
  }
}

/** Impulse/Expanse 테마 CartDrawer 가 listen 하는 이벤트 (theme.js) */
function notifyThemeCartAdded(product: unknown): void {
  document.dispatchEvent(
    new CustomEvent("ajaxProduct:added", {
      bubbles: true,
      detail: { product },
    })
  );
}

/**
 * Shopify AJAX Cart API 로 카트에 라인 추가.
 * Storefront API 의 variant gid 를 numeric id 로 변환해 사용.
 *
 * 성공 시 테마 CartDrawer 가 listen 하는 `ajaxProduct:added` 를 디스패치해
 * 헤더 카운트·드로어 UI 를 새로고침 없이 갱신한다.
 */
export async function addVariantToCart(
  config: PocaConfig,
  variantGid: string,
  quantity = 1
): Promise<AddToCartResult> {
  const id = gidToNumericId(variantGid);
  try {
    const res = await fetch(config.routesCartAdd, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        items: [{ id: Number.parseInt(id, 10), quantity }],
      }),
    });

    if (!res.ok) {
      const text = await res.text();
      return {
        ok: false,
        message: text
          ? parseCartAddErrorMessage(text)
          : `Could not add to cart (${res.status})`,
      };
    }

    const product = await res.json();
    notifyThemeCartAdded(product);
    return { ok: true };
  } catch (e) {
    return {
      ok: false,
      message: e instanceof Error ? e.message : "Unknown error",
    };
  }
}
